import { SharedLogSearchRequestQueue } from "../utils/logSearchRequests.js";
import {
    ProjectionLog,
    ProjectionQuery,
    ProjectionQueryProvider,
    ProjectionQueryResult,
    ProjectionWindow,
} from "./projectionEngine.js";

type LogSearchQueue = {
    fetchJson(url: string, options?: RequestInit): Promise<{
        error?: string;
        result?: ProjectionLog[];
        members?: Array<{ id: string }>;
        channels?: Array<{ id: string }>;
    }>;
};

type LogSearchProjectionProviderOptions = {
    logSearchQueue?: LogSearchQueue;
    limit?: number;
    maxPages?: number;
};

function createAbortError(): DOMException {
    return new DOMException("The operation was aborted.", "AbortError");
}

function throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted)
        throw createAbortError();
}

function normalizeWindow(window: ProjectionWindow): Required<Pick<ProjectionWindow, "after" | "before">> {
    return {
        after   : Math.max(0, Number(window.from ?? window.after ?? 0)),
        before  : Number(window.to ?? window.before ?? Date.now()),
    };
}

export class LogSearchProjectionProvider implements ProjectionQueryProvider<ProjectionLog> {
    readonly logSearchQueue: LogSearchQueue;
    readonly limit: number;
    readonly maxPages: number;

    constructor({
        logSearchQueue = SharedLogSearchRequestQueue,
        limit = 25,
        maxPages = 20,
    }: LogSearchProjectionProviderOptions = {}) {
        this.logSearchQueue = logSearchQueue;
        this.limit = limit;
        this.maxPages = maxPages;
    }

    async fetchAll({
        query,
        window,
        signal,
    }: {
        query: Required<ProjectionQuery>;
        window: ProjectionWindow;
        signal?: AbortSignal;
    }): Promise<ProjectionQueryResult<ProjectionLog>> {
        const normalizedWindow = normalizeWindow(window);
        const logs: ProjectionLog[] = [];
        const members: Array<{ id: string }> = [];
        const channels: Array<{ id: string }> = [];
        let after = normalizedWindow.after;

        for (let page = 0; page < this.maxPages; page++) {
            throwIfAborted(signal);

            const result = await this.fetchPage({
                expression  : query.expression,
                after,
                before      : normalizedWindow.before,
                signal,
            });

            logs.push(...result.logs);
            members.push(...result.members);
            channels.push(...result.channels);

            if (result.logs.length < this.limit)
                return {
                    logs,
                    members,
                    channels,
                    partial: false,
                };

            const lastLog = result.logs.at(-1);

            if (!lastLog || lastLog.createdAt <= after)
                break;

            after = lastLog.createdAt;
        }

        return {
            logs,
            members,
            channels,
            partial: true,
        };
    }

    async fetchPage({
        expression,
        after,
        before,
        signal,
    }: {
        expression: string;
        after: number;
        before: number;
        signal?: AbortSignal;
    }) {
        const params = new URLSearchParams({
            after   : String(after),
            before  : String(before),
            limit   : String(this.limit),
            orderBy : "ASC",
        });
        const response = await this.logSearchQueue.fetchJson(`/api/logs?${params.toString()}`, {
            headers: {
                "content-type": "application/json",
            },
            method  : "POST",
            signal,
            body    : JSON.stringify({
                mode    : "expression",
                query   : expression,
            }),
        });

        if (response.error)
            throw new Error(response.error);

        return {
            logs        : response.result ?? [],
            members     : response.members ?? [],
            channels    : response.channels ?? [],
        };
    }
}
